import { z } from "zod";

/** The composer-and-fixes round (2026-11-03, brief docs/briefs/composer-and-fixes-round.md part one): a person's reply in progress, kept on
 *  the ticket for that person alone (`reply_drafts`, migration 0129). It holds the text and the person's choices; never the reply's private
 *  site fields or file ids (finding 10, the strict schema refuses them), never pictures (finding 9, the server strips them). A blank text
 *  deletes the kept draft (finding 11). */

/** The To line as the composer holds it: the way (mail or the site) and the three address lists. */
export const ReplyDraftToSchema = z.object({
  via: z.enum(["mail", "site"]),
  to: z.array(z.string().max(320)).max(50),
  cc: z.array(z.string().max(320)).max(50),
  bcc: z.array(z.string().max(320)).max(50),
}).strict();
export type ReplyDraftTo = z.infer<typeof ReplyDraftToSchema>;

const SendAs = z.enum(["open", "pending", "resolved"]);

/** `PUT …/tickets/:number/reply-draft`: strict, so `fields`, `attachmentIds` or any other key is a 400. */
export const ReplyDraftPutSchema = z.object({
  html: z.string().max(200_000),
  text: z.string().max(50_000),
  /** null: the To line untouched (the default recipients) */
  to: ReplyDraftToSchema.nullable().optional(),
  /** the status the send would set; null: the person's default */
  sendAs: SendAs.nullable().optional(),
  signature: z.boolean().optional(),
  /** the AI draft the text started from (an edited AI draft) */
  aiDraftId: z.string().min(1).max(100).nullable().optional(),
  /** files were attached when it was kept; they are not kept ("Files were not kept") */
  hadFiles: z.boolean().optional(),
}).strict();
export type ReplyDraftPut = z.infer<typeof ReplyDraftPutSchema>;

/** What the three doors answer: the kept draft, or null for none. */
export const ReplyDraftSchema = z.object({
  html: z.string(),
  text: z.string(),
  to: ReplyDraftToSchema.nullable(),
  sendAs: SendAs.nullable(),
  signature: z.boolean(),
  aiDraftId: z.string().nullable(),
  hadFiles: z.boolean(),
  updatedAt: z.string(),
});
export type ReplyDraft = z.infer<typeof ReplyDraftSchema>;
export const ReplyDraftAnswerSchema = z.object({ draft: ReplyDraftSchema.nullable() });

/** A kept draft never holds a picture: a pasted picture is an upload the hourly sweep removes after a day (finding 9). */
export const stripDraftPictures = (html: string): string => html.replace(/<img\b[^>]*>/gi, "");
